import { createActionGroup, emptyProps, props } from '@ngrx/store';
import {
    Clinician,
    Facility,
    GSA,
    Houseware,
    Insurance,
    Job,
    MarginCalculatorModel,
    BillingRate,
    Reimbursement,
    ProfessionSpecialtyModel,
    MarginsDifferences,
    MarginStateInsuranceEffectiveDates,
    FacilityMinimumWage,
    ProfessionSpecialties,
    MinimumWageDetails
} from '../../models';
import { UserCredentials } from '../../services/margin-calculator-auth.service';
import { FacilityContractRules } from 'src/app/facility-contracts/shared/models';
import { MoneyMovementValidation } from '../../models/money-movement-validation.model';

export const MarginCalculatorApiActions = createActionGroup({
    source: 'Margin Calculator API',
    events: {
        marginLoadedSuccess: props<{ margin: MarginCalculatorModel }>(),
        marginLoadedFail: props<{ message: string }>(),

        userCredentialsLoadedSuccess: props<{ userCredentials: UserCredentials }>(),
        userCredentialsLoadedFail: props<{ message: string }>(),

        marginsDifferencesLoadedSuccess: props<{ marginsDifferences: MarginsDifferences[] }>(),
        marginsDifferencesLoadedFail: props<{ message: string }>(),

        jobLoadedSuccess: props<{ job: Job }>(),
        jobLoadedFail: props<{ message: string }>(),

        specialtyLoadedSuccess: props<{ specialty: ProfessionSpecialtyModel }>(),
        specialtyLoadedFail: props<{ message: string }>(),

        clinicianLoadedSuccess: props<{ clinician: Clinician }>(),
        clinicianLoadedFail: props<{ message: string }>(),

        clinicianNamesLoadedSuccess: props<{ clinicians: Clinician[] }>(),
        clinicianNamesLoadedFail: props<{ message: string }>(),

        clinicianEmailsLoadedSuccess: props<{ clinicians: Clinician[] }>(),
        clinicianEmailsLoadedFail: props<{ message: string }>(),

        facilityLoadedSuccess: props<{ facility: Facility; dirtyForm: boolean }>(),
        facilityLoadedFail: props<{ message: string }>(),

        facilityLoadedByNameSuccess: props<{ facility: Facility }>(),
        findFacilityByNameFail: emptyProps(),

        facilitiesLoadedSuccess: props<{ facilities: Facility[] }>(),
        facilitiesLoadedFail: props<{ message: string }>(),

        billingRatesLoadedSuccess: props<{ billingRates: BillingRate[] }>(),
        billingRatesLoadedFail: props<{ message: string }>(),

        reimbursementsLoadedSuccess: props<{ reimbursements: Reimbursement }>(),
        reimbursementsLoadedFail: props<{ message: string }>(),

        loadGSARates: props<{ facilityId: number; startDate: Date; endDate: Date }>(),
        gsaRatesLoadedSuccess: props<{ gsaRates: GSA }>(),
        gsaRatesLoadedFail: props<{ message: string }>(),

        getContractEndDate: props<{ startDate: Date; weeks: number }>(),
        getContractEndDateLoadedSuccess: props<{ endDate: string }>(),
        getContractEndDateLoadedFail: props<{ message: string }>(),

        getContractDates: props<{ startDate: Date; contractDays: number; shifts: number }>(),
        getContractDatesLoadedSuccess: props<{ endDate: string }>(),
        getContractDatesLoadedFail: props<{ message: string }>(),

        housewareListLoadedSuccess: props<{ housewares: Houseware[] }>(),
        housewareListLoadedFail: props<{ message: string }>(),

        insuranceInfoLoadedSuccess: props<{ insuranceInfo: Insurance }>(),
        insuranceInfoLoadedFail: props<{ message: string }>(),

        loadFacilityContractRules: props<{ contractGroupSystemId: number }>(),
        facilityContractRulesLoadedSucess: props<{ facilityContractRules: FacilityContractRules }>(),
        facilityContractRulesLoadedFail: props<{ message: string }>(),

        insuranceEffectiveDatesLoadedSuccess: props<{
            marginStateInsuranceEffectiveDates: MarginStateInsuranceEffectiveDates;
        }>(),
        insuranceEffectiveDatesLoadedFail: props<{ message: string }>(),

        getContractLengthWithBookingWeeks: props<{
            shiftType: number;
            startDate: Date;
            endDate: Date;
            vacationDates: Date[];
            shifts: number;
        }>(),
        getContractLengthWithBookingWeeksLoadedSuccess: props<{
            weeks: number;
            lastWeeksLength: number;
            bookingWeeks: number;
            altBookingWeeks: number;
        }>(),
        getContractLengthWithBookingWeeksLoadedFail: props<{ message: string }>(),

        targetMarginLoadedSuccess: props<{ targetMargin: number }>(),
        targetMarginLoadedFail: props<{ message: string }>(),

        targetMarginWithProfessionLoadedSuccess: props<{ targetMargin: number }>(),
        targetMarginWithProfessionLoadedFail: props<{ message: string }>(),

        targetMarginWithExpertiseLoadedSuccess: props<{ targetMargin: number }>(),
        targetMarginWithExpertiseLoadedFail: props<{ message: string }>(),

        facilityMinimumWageLoadedSuccess: props<{ facilityMinimumWage: FacilityMinimumWage }>(),
        facilityMinimumWageLoadedFail: props<{ message: string }>(),

        minimumWageDetailsLoadedSuccess: props<{ minimumWageDetails: MinimumWageDetails }>(),
        minimumWageDetailsLoadedFail: props<{ message: string }>(),

        getContractRequestCountSuccess: props<{ count: number }>(),
        getContractRequestCountFail: props<{ message: string }>(),

        premiumBillingRateLoadedSuccess: props<{ premiumBillRate: number; marginPremiumBillRate: number }>(),
        premiumBillingRateLoadedFail: props<{ message: string }>(),

        resetApiCallResponses: emptyProps(),
        resetGetPremiumBillRateResponseState: emptyProps(),

        professionWithSpecialtiesLoadedSuccess: props<{ professionWithSpecialties: ProfessionSpecialties }>(),
        professionWithSpecialtiesLoadedFail: props<{ message: string }>(),

        PreviousMarginIdLoadedSuccess: props<{ previousMarginId: number }>(),
        PreviousMarginIdLoadedFail: props<{ message: string }>(),

        loadMoneyMovementValidations: props<{ marginId: number; previousMarginId: number }>(),
        moneyMovementValidationsLoadedSuccess: props<{ moneyMovementValidations: MoneyMovementValidation[] }>(),
        moneyMovementValidationsLoadedFail: props<{ message: string }>(),

        loadOfferAyaPaidBonusAmount: props<{ offerId: number }>(),
        offerAyaPaidBonusLoadedSuccess: props<{ offerAyaPaidBonus: number }>(),
        offerAyaPaidBonusLoadedFail: props<{ message: string }>()
    }
});
